import React, { useEffect, useMemo, useRef } from 'react';
import { Box, CssBaseline, Drawer } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { shellTokens } from './shellTokens';
import { ShellProvider, useShell } from './ShellContext';
import { useNavModel } from './useNavModel';
import { Sidebar, RailFlyout } from './Sidebar';
import { TopBar } from './TopBar';
import { TrialStatusProvider } from './useTrialStatus';
import { ProfileMenu } from './ProfileMenu';
import { CommandPalette } from './CommandPalette';
import { NotificationsMenu } from './NotificationsMenu';
import { useIsMobile } from '../../hooks/useIsMobile';
import { RegionMenu } from './RegionMenu';
import { ImpersonationBanner } from './ImpersonationBanner';
import { useAuth } from '../../contexts/AuthContext';
import { useAIAgent } from '../../contexts/AIAgentContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { useOrganizations } from '../../contexts/OrganizationContext';
import { apiService } from '../../services/api';
import { ApiKeyBanner } from '../ApiKeyBanner';
import { PendingApprovalBanner } from '../PendingApprovalBanner';
import { AppErrorBoundary } from '../AppErrorBoundary';
import AIAgentSidebar from '../AIAgentSidebar';
import { useShellTranslation } from './useShellTranslation';
import { UpdateToast } from '../appUpdate';

const { size } = shellTokens;

/**
 * The shell's frame containers must never scroll, not even programmatically.
 * `overflow: hidden` still leaves a scroll offset that focus, `scrollIntoView`
 * or a stray overflowing child can move, which slid the header off-screen and
 * exposed a grey strip under the sidebar. `overflow: clip` forbids that; the
 * hidden fallback covers browsers without it. Only the content pane below
 * scrolls.
 */
const frameClipSx = {
  overflow: 'hidden',
  '@supports (overflow: clip)': { overflow: 'clip' },
} as const;

/** Two letters at most, so the 26px identity avatar never has to shrink type. */
const initialsOf = (value: string): string => {
  const words = value.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

const PAGES_WITH_API_KEY_BANNER = ['/app/api-keys', '/app/api-test'];

const ShellBody: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useShellTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const nav = useNavModel();
  const { setOverlay, overlay, mobileOpen, setMobileOpen } = useShell();
  const { currentUser, userProfile, isAdmin } = useAuth();
  const { isOpen: aiOpen, toggleSidebar: toggleAi, closeSidebar: closeAi } = useAIAgent();
  const { isImpersonating, impersonatedRole, impersonatedOrg, impersonatedBrand, stopImpersonation } =
    useImpersonation();

  // Only a real platform admin may impersonate. `isAdmin` already comes from the
  // real profile rather than the effective one, so no impersonation guard is
  // needed here — and adding one killed ⌘⇧U mid-session while the banner's
  // Switch went on offering the very same palette.
  const canViewAs = isAdmin;

  // The organization the shell is working in: the one this person switched to
  // when they belong to several, their own otherwise.
  const { actingOrganization, actingOrganizationId } = useOrganizations();
  const organizationId =
    actingOrganizationId || userProfile?.organizationId || userProfile?.primaryOrganizationId;
  const { data: organization } = useQuery({
    queryKey: ['shell-organization', organizationId],
    // GET /organizations/:id answers with the { success, data } envelope.
    queryFn: async () => {
      const response = await apiService.getOrganization(organizationId as string);
      return response?.data ?? response;
    },
    enabled: Boolean(organizationId) && !isImpersonating && nav.role !== 'admin',
    staleTime: 5 * 60 * 1000,
    // The identity block degrades to the role label; a retry storm helps nobody.
    retry: false,
  });

  const identity = useMemo(() => {
    const impersonatedName = impersonatedOrg?.name || impersonatedBrand?.name;
    const title =
      impersonatedName ||
      (nav.role === 'admin'
        ? 'Motzu Labs'
        : actingOrganization?.name ||
          organization?.name ||
          organization?.displayName ||
          userProfile?.vendorInfo?.companyName ||
          currentUser?.displayName ||
          currentUser?.email ||
          '');
    return {
      title,
      subtitle: t(`shell.identity.${nav.role}`),
      initials: initialsOf(title || '?'),
    };
  }, [
    impersonatedOrg,
    impersonatedBrand,
    nav.role,
    actingOrganization,
    organization,
    userProfile,
    currentUser,
    t,
  ]);

  const profile = useMemo(() => {
    const realName = currentUser?.displayName || currentUser?.email || t('shell.profile.account');
    return {
      // While impersonating, the footer chip says whose session this really is,
      // so a screenshot is not mistaken for the store's own view (MOT-106 S00-5).
      name: isImpersonating
        ? t('shell.profile.signedInAs', { name: realName, defaultValue: 'Signed in as {{name}}' })
        : realName,
      realName,
      role: t(`shell.role.${isAdmin ? 'admin' : nav.role}`),
      initial: initialsOf(realName).slice(0, 1),
    };
  }, [currentUser, isAdmin, isImpersonating, nav.role, t]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const meta = event.metaKey || event.ctrlKey;
      if (!meta) return;
      const key = event.key.toLowerCase();
      if (key === 'k') {
        event.preventDefault();
        setOverlay({ kind: 'palette', mode: 'nav' });
      } else if (key === 'u' && event.shiftKey && canViewAs) {
        event.preventDefault();
        setOverlay({ kind: 'palette', mode: 'as' });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setOverlay, canViewAs]);

  /**
   * A banner that outlives its context is worse than no banner: reaching admin
   * routes while the banner claims you are a store user must not stand.
   *
   * The session's first render is the exception. Every entry point starts
   * impersonation and *then* navigates, and several of them live on admin
   * routes themselves — Store Management and the brand dashboard's "View as the
   * vendor" — so treating that opening frame as leaving the workspace killed
   * the session before it began. There the answer is to redirect, not to exit;
   * it doubles as a backstop for any entry point that forgets to navigate.
   * Navigating into an admin route later is a deliberate departure, and ends it.
   */
  const impersonationOriginRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isImpersonating) {
      impersonationOriginRef.current = null;
      return;
    }
    const onAdminRoute = location.pathname.startsWith('/app/admin');
    if (impersonationOriginRef.current === null) {
      impersonationOriginRef.current = location.pathname;
      if (onAdminRoute && impersonatedRole !== 'admin') {
        navigate('/app/dashboard', { replace: true });
      }
      return;
    }
    if (impersonationOriginRef.current === location.pathname) return;
    // The session has moved off its origin; the origin is no longer protected.
    impersonationOriginRef.current = '';
    if (onAdminRoute && impersonatedRole !== 'admin') {
      // stopImpersonation cancels and clears the account-scoped cache itself.
      stopImpersonation();
    }
  }, [isImpersonating, impersonatedRole, location.pathname, stopImpersonation, navigate]);

  const sidebar = (
    <Sidebar nav={nav} isMobile={isMobile} identity={identity} profile={profile} />
  );

  return (
    <TrialStatusProvider enabled={nav.role === 'store'}>
      <Box
        sx={{
          // Pinned to the viewport so the shell can never add height to the
          // document: the window itself has nothing to scroll on any page.
          position: 'fixed',
          top: 0,
          left: 0,
          display: 'flex',
          flexDirection: 'column',
          height: { xs: '100dvh', md: '100vh' },
          '@supports not (height: 100dvh)': { height: '100vh' },
          width: '100%',
          maxWidth: '100vw',
          boxSizing: 'border-box',
          ...frameClipSx,
        }}
      >
        <CssBaseline />
        <ImpersonationBanner />

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, ...frameClipSx }}>
          {!isMobile && sidebar}
          {isMobile && (
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
              // A shell overlay renders as a sibling of the Drawer, outside its
              // portal, so the Drawer's focus enforcement would keep yanking
              // focus back and defeat the overlay's own trap.
              ModalProps={{ keepMounted: true, disableEnforceFocus: overlay !== null }}
              sx={{
                '& .MuiDrawer-paper': {
                  width: size.mobileDrawer,
                  maxWidth: '100%',
                  border: 'none',
                  boxSizing: 'border-box',
                },
              }}
            >
              {sidebar}
            </Drawer>
          )}

          <Box
            component="main"
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.default',
              // The update toast is positioned against this content area.
              position: 'relative',
              ...frameClipSx,
            }}
          >
            <TopBar nav={nav} isMobile={isMobile} canViewAs={canViewAs} onAskMozi={toggleAi} />
            <Box
              data-scroll-reset
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <PendingApprovalBanner />
              {PAGES_WITH_API_KEY_BANNER.some((path) => location.pathname.startsWith(path)) && (
                <ApiKeyBanner />
              )}
              <AppErrorBoundary resetKey={location.pathname}>{children}</AppErrorBoundary>
            </Box>
            <UpdateToast />
          </Box>
        </Box>

        <RailFlyout nav={nav} />
        <ProfileMenu
          name={profile.realName}
          email={currentUser?.email || ''}
          role={profile.role}
          canViewAs={canViewAs}
        />
        <NotificationsMenu />
        <RegionMenu />
        <CommandPalette nav={nav} canViewAs={canViewAs} />
        <AIAgentSidebar
          open={aiOpen}
          onClose={closeAi}
          userName={currentUser?.displayName || undefined}
          page={nav.activeLeaf ? { label: t(`shell.item.${nav.activeLeaf.labelKey}`), icon: nav.activeLeaf.icon } : null}
        />
      </Box>
    </TrialStatusProvider>
  );
};

export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ShellProvider>
    <ShellBody>{children}</ShellBody>
  </ShellProvider>
);
